import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Label } from "@/components/ui";
import { FeedbackSettings } from "@/components/feedback/FeedbackSettings";
import { ShieldCheck, FileText, MonitorSmartphone, CreditCard, UserPlus, Mail, LogOut, ChevronRight, Heart } from "lucide-react";

const LINKS = [
  { to: "/app/privacy", icon: ShieldCheck, title: "Privacy & consent", sub: "Manage HIPAA ambient audio authorization", testid: "account-privacy" },
  { to: "/app/patients", icon: Heart, title: "Care recipients", sub: "Manage loved ones, insurance, and conditions", testid: "account-patients" },
  { to: "/app/inbox", icon: Mail, title: "Inbox", sub: "Messages from your KinBridge advocate", testid: "account-inbox" },
  { to: "/app/invite", icon: UserPlus, title: "Invite circle member", sub: "Family, aide, or viewer", testid: "account-invite" },
  { to: "/app/sessions", icon: MonitorSmartphone, title: "Session activity", sub: "View and revoke signed-in devices", testid: "account-sessions" },
  { to: "/app/billing", icon: CreditCard, title: "Plans & billing", sub: "$15 Basic / $30 Premium · 14-day trial", testid: "account-billing" },
  { to: "/legal", icon: FileText, title: "Legal & policies", sub: "Privacy, Terms, HIPAA, CCPA, DSAR, EULA", testid: "account-legal" },
];

export default function Account() {
  const { user, family, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);

  useEffect(() => { api.listPatients().then((r) => setPatients(r.items || [])).catch(() => {}); }, []);
  const primary = patients.find((p) => p.is_primary) || patients[0];
  const extras = patients.filter((p) => p.id !== primary?.id).length;

  const doSignOut = async () => { await signOut(); navigate("/login"); };

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="More" title="Account & settings" />

      {family && (
        <Card className="mb-5">
          <Label>Your family</Label>
          <div className="font-display text-xl text-ink mt-1">{family.name || "—"}</div>
          <div className="text-sm text-subtle mt-0.5">Caring for {primary?.name || family.loved_one?.name || "—"}{extras > 0 ? ` + ${extras} more` : ""}</div>
          <div className="border-t border-line mt-4 pt-3">
            <Label>Members ({family.members?.length || 0})</Label>
            <div className="mt-2 space-y-2">
              {(family.members || []).map((m) => (
                <div key={m.user_id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-sm font-semibold">{m.name?.[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0"><div className="text-[15px] text-ink truncate">{m.name}</div><div className="text-xs text-subtle capitalize">{m.role.replace(/_/g, " ")}</div></div>
                </div>
              ))}
              {(family.pending_invites || []).map((p) => (
                <div key={p.email} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-tint flex items-center justify-center"><Mail className="w-4 h-4 text-brand-dark" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] text-ink truncate">{p.email}</div>
                    <div className="text-xs text-subtle">
                      {(!p.expires_at || new Date(p.expires_at) < new Date())
                        ? <span className="text-amber-700">Never delivered — resend it from Invite a member</span>
                        : <>Invite pending · {p.role.replace(/_/g, " ")}</>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="bg-white border border-line rounded-xl overflow-hidden">
        {LINKS.map((it, i) => (
          <Link key={it.to} to={it.to} data-testid={it.testid}
            className={`flex items-center gap-3 p-4 hover:bg-brand-tint transition-colors ${i > 0 ? "border-t border-line" : ""}`}>
            <div className="w-9 h-9 rounded-lg bg-brand-tint flex items-center justify-center shrink-0"><it.icon className="w-[18px] h-[18px] text-brand-dark" /></div>
            <div className="flex-1 min-w-0"><div className="font-semibold text-ink">{it.title}</div><div className="text-xs text-subtle">{it.sub}</div></div>
            <ChevronRight className="w-4 h-4 text-subtle" />
          </Link>
        ))}
      </div>

      <FeedbackSettings />

      <Card className="mt-5">
        <Label>Signed in as</Label>
        <div className="flex items-center gap-3 mt-2">
          <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center text-white font-semibold">{user?.name?.[0]?.toUpperCase()}</div>
          <div className="flex-1 min-w-0"><div className="font-semibold text-ink truncate">{user?.name}</div><div className="text-xs text-subtle truncate">{user?.email}{role ? ` · ${role.replace(/_/g, " ")}` : ""}</div></div>
        </div>
        <Button variant="danger" className="w-full mt-4" data-testid="account-signout" onClick={doSignOut}><LogOut className="w-4 h-4" /> Sign out</Button>
      </Card>

      <p className="text-center text-xs text-subtle mt-6">KinBridge Health · HIPAA-conscious care coordination</p>
    </div>
  );
}
